/**
 * Tiquete o factura: quién lo decide y cómo se llama en pantalla.
 *
 * En Costa Rica una venta de mostrador se emite como **tiquete electrónico** y
 * no como factura. Son dos comprobantes distintos ante Hacienda —tipo 04 y tipo
 * 01 de la clave numérica— y hasta ahora el terminal no distinguía: todo salía
 * con el valor por defecto del campo de Business Central, que es «Factura».
 *
 * Acordado en `contrato-tipo-documento-electronico.md`, ronda 8:
 *
 *   · el **cliente** pone el valor inicial — el genérico de la tienda o uno de
 *     tipo «No Contribuyente» dan tiquete; cualquier otro cliente identificado
 *     da factura;
 *   · el **cajero** puede cambiarlo;
 *   · el valor **se congela en el alta**, así que un reintento del mismo
 *     documento manda siempre lo mismo.
 *
 * ## Por qué puede no decidirse
 *
 * La regla necesita saber cuál es el cliente por defecto de la tienda, y ese
 * dato lo publica Business Central en la ficha de tienda. Mientras no llegue,
 * `tipoInicialDeComprobante` devuelve `null` **a propósito**: la clave no viaja,
 * y BC aplica su regla de respaldo —`Venta TPV = true` ⇒ tiquete—, que es
 * exactamente el comportamiento de hoy.
 *
 * Decidir sin ese dato sería peor que no decidir: el cliente genérico de Costa
 * Rica es `VENTA CASUAL Y/O DE CONTADO`, de tipo «Legal Entity», así que sin
 * saber que es el de la tienda la regla lo tomaría por un cliente identificado
 * y mandaría **factura** en la venta de mostrador — al revés de lo que toca.
 */
import { COUNTRY_CODES } from "../../constants/countries";
import { getIdentityDocument } from "../../constants/identityDocuments";

/** Los dos valores que admite Business Central, por nombre y no por código. */
export const TIPOS_DE_COMPROBANTE = {
  TIQUETE: "Tiquete",
  FACTURA: "Factura",
};

/** Hoy sólo Costa Rica separa el tiquete de la factura. */
export const paisDistingueTiquete = (countryCode) => countryCode === COUNTRY_CODES.COSTARICA;

/**
 * Si el cliente no aporta identificación fiscal.
 *
 * Es el tipo que Hacienda reconoce para eso, y el que se acordó usar cuando un
 * comprador quiere el tiquete a su nombre sin dar cédula. Ver el anexo de los
 * comodines del contrato.
 */
const esNoContribuyente = (cliente, countryCode) => {
  const tipo = String(cliente?.Tipo_Documento_SrI ?? "").trim();
  if (!tipo) return false;
  const catalogo = getIdentityDocument(countryCode);
  return tipo === catalogo?.noContribuyente?.value;
};

/**
 * El valor con el que nace el documento, antes de que el cajero opine.
 *
 * @returns {string|null} `"Tiquete"`, `"Factura"`, o `null` cuando no se puede
 *   decidir — el país no distingue, o todavía no se sabe cuál es el cliente por
 *   defecto de la tienda.
 */
export const tipoInicialDeComprobante = ({ countryCode, cliente, clientePorDefectoDeLaTienda }) => {
  if (!paisDistingueTiquete(countryCode)) return null;

  // Sin el cliente por defecto no hay regla: se deja decidir a Business Central.
  const porDefecto = String(clientePorDefectoDeLaTienda ?? "").trim();
  if (!porDefecto) return null;

  const noDelCliente = String(cliente?.No ?? "").trim();
  if (!noDelCliente) return TIPOS_DE_COMPROBANTE.TIQUETE;
  if (noDelCliente === porDefecto) return TIPOS_DE_COMPROBANTE.TIQUETE;
  if (esNoContribuyente(cliente, countryCode)) return TIPOS_DE_COMPROBANTE.TIQUETE;

  return TIPOS_DE_COMPROBANTE.FACTURA;
};

/**
 * Cómo se llama el documento en pantalla y en los mensajes.
 *
 * Estaba escrito tres veces —el listado, el formulario y la cabecera— con la
 * misma cadena de condiciones repetida. Al aparecer un cuarto nombre, «Tiquete»,
 * mantener tres copias sincronizadas deja de ser sostenible.
 */
export const rotulosDelDocumento = ({ documentType, tipoDeComprobante } = {}) => {
  if (documentType === "Credit Memo") {
    return {
      docLabel: "Nota de crédito",
      docLabelLower: "nota de crédito",
      docLabelPlural: "notas de crédito",
      genero: "f",
    };
  }
  if (documentType === "Boleta") {
    return { docLabel: "Boleta", docLabelLower: "boleta", docLabelPlural: "boletas", genero: "f" };
  }
  if (tipoDeComprobante === TIPOS_DE_COMPROBANTE.TIQUETE) {
    return {
      docLabel: "Tiquete",
      docLabelLower: "tiquete",
      docLabelPlural: "tiquetes",
      genero: "m",
    };
  }
  return { docLabel: "Factura", docLabelLower: "factura", docLabelPlural: "facturas", genero: "f" };
};

/**
 * Un participio que concuerde con el documento.
 *
 * «Tiquete creada pero no contabilizada» es lo que salía, porque el participio
 * estaba escrito a mano en la frase y los cuatro nombres no comparten género.
 * Se pide en masculino --«creado»-- y se devuelve como toque.
 *
 * @param {string} enMasculino  «creado», «guardado», «enviado»…
 * @param {{documentType?: string, tipoDeComprobante?: string}} documento
 * @returns {string} «creado» para el tiquete, «creada» para los demás.
 */
export const concordado = (enMasculino, documento = {}) => {
  const { genero } = rotulosDelDocumento(documento);
  if (genero === "m") return enMasculino;
  return enMasculino.replace(/o$/, "a");
};

/**
 * El documento con su artículo, para meterlo dentro de una frase.
 *
 * Los mensajes de error decían siempre «la factura», también al sincronizar un
 * abono o un tiquete. Como los cuatro nombres no comparten género —«la
 * factura», «el tiquete»—, el artículo tiene que venir con el nombre y no
 * pegarse aparte.
 *
 * @returns {string} «la factura», «el tiquete», «la boleta», «la nota de crédito».
 */
export const elDocumento = ({ documentType, tipoDeComprobante } = {}) => {
  const { docLabelLower } = rotulosDelDocumento({ documentType, tipoDeComprobante });
  return `${docLabelLower === "tiquete" ? "el" : "la"} ${docLabelLower}`;
};

/**
 * El documento detrás de un «de», ya contraído.
 *
 * «de + el» es «del» en castellano, así que no basta con pegar `elDocumento`
 * detrás de la preposición: saldría «de el tiquete». Y como el género no lo
 * comparten los cuatro nombres, tampoco vale fijar uno de los dos.
 *
 * @returns {string} «del tiquete», «de la factura», «de la nota de crédito».
 */
export const delDocumento = ({ documentType, tipoDeComprobante } = {}) => {
  const con = elDocumento({ documentType, tipoDeComprobante });
  return con.startsWith("el ") ? `del ${con.slice(3)}` : `de ${con}`;
};

/**
 * Cómo se nombra el documento que corrige un abono.
 *
 * En pantalla decía «Factura corregida» siempre, también cuando lo que se
 * devolvía era un tiquete. El abono nace copiando el documento que corrige, así
 * que se lleva su `Electronic_Document_Type`: el dato está, sólo faltaba usarlo.
 *
 * El participio concuerda con el nombre --«tiquete corregido», «factura
 * corregida»--, y por eso vive aquí y no pegado en la pantalla: es la misma
 * regla de género que obligó a que el artículo viajara con el nombre.
 *
 * @returns {string} «Tiquete corregido», «Factura corregida», «Boleta corregida».
 */
export const rotuloDelCorregido = ({ tipoDeComprobante, documentType = "Invoice" } = {}) => {
  const { docLabel, docLabelLower } = rotulosDelDocumento({ documentType, tipoDeComprobante });
  return `${docLabel} corregid${docLabelLower === "tiquete" ? "o" : "a"}`;
};

/**
 * Qué clase de comprobante corrige un abono, si se sabe.
 *
 * En el listado de abonos todos decían «Nota de crédito» y no había forma de
 * distinguir el que devuelve un tiquete del que devuelve una factura — que ante
 * Hacienda son dos documentos distintos.
 *
 * Devuelve `null` cuando **no se sabe**, y eso es la mitad del valor: un abono
 * creado en el terminal hereda el tipo del documento que corrige, pero uno
 * traído por Traer Históricos no lo trae, porque `PostedSalesCreditMemo` no
 * publica el campo. Inventar «Factura» en ese caso sería afirmar algo que nadie
 * comprobó; el listado prefiere decir sólo «Nota de crédito».
 *
 * @returns {string|null} «Tiquete», «Factura», «Boleta», o `null`.
 */
export const comprobanteQueCorrige = ({ documentType, tipoDeComprobante } = {}) => {
  if (documentType !== "Credit Memo") return null;
  if (!tipoDeComprobante) return null;
  return rotulosDelDocumento({ documentType: "Invoice", tipoDeComprobante }).docLabel;
};
